import React, { useState } from 'react';
import { 
  Box, 
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useNavigate } from "react-router-dom";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { createBoxAssets } from '../apis/apiList/boxAssetsApi';
import { useParams } from "react-router-dom";
import { useMessage } from '../context/MessageContext';

const folderSchema = Yup.object().shape({
    folderName: Yup.string()
      .required('Folder name is required')
      .max(255, 'Folder name cannot exceed 255 characters')
      .test(
        'no-slashes',
        'Folder name cannot contain forward or backward slashes (/, \\)',
        value => value && !value.includes('/') && !value.includes('\\')
      )
      .test(
        'no-trailing-spaces',
        'Folder name cannot have trailing spaces',
        value => value && value.trim() === value
      )
      .test(
        'no-dot-folders',
        'Folder names "." and ".." are not allowed',
        value => value !== '.' && value !== '..'
      )
      .test(
        'printable-ascii-only',
        'Folder name can only contain printable ASCII characters',
        value => {
          if (!value) return true;
          // Check for printable ASCII characters (codes 32-126)
          return [...value].every(char => {
            const code = char.charCodeAt(0);
            return code >= 32 && code <= 126;
          });
        }
      ),
    description: Yup.string()
      .max(500, 'Description cannot exceed 500 characters')
  });

const CreateFolder = () => {
  const { id, boxFolderId } = useParams();
  const { showMessage } = useMessage();
  const initialValues = {
    folderName: '',
    description: ''
  };

  const navigate = useNavigate();

  const handleSubmit = async (values, { setValues, setSubmitting, resetForm }) => {
    console.log('Form submitted:', values);

    try{
      let data = {
        parentid: boxFolderId,
        foldername: values.folderName,
        folderdesc: values.description, 
      }
      const folderRes = await createBoxAssets(data);
      console.log('folder Response', folderRes);
      if(folderRes){
        showMessage(folderRes.message, 'success')
        resetForm();
        // setValues({
        //   folderName: "",
        //   description: "",
        // });
        setSubmitting(false);
      }
    } catch (err) {
      console.log('error', err);
      if (err?.response) {
        showMessage(err?.response?.data?.message, 'error');
      } else {
          showMessage(err?.message, 'error');
      }
    }
  };

  const handleBack = () => {
    navigate(`/org/${id}/assets`);
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: 'white' }}>
        <Toolbar onClick={() => handleBack}>
          <IconButton
            edge="start"
            color="primary"
            aria-label="back to assets"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Button 
            color="primary" 
            sx={{ textTransform: 'none' }}
          >
            Back To Assets
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
    <Box elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={folderSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            isValid,
            dirty
          }) => (
            <Form>
              <Typography variant="h3" sx={{ mb: 3 }}>
                Create New Folder
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="folderName"
                label="Folder Name"
                name="folderName"
                autoFocus
                placeholder="New Folder"
                value={values.folderName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.folderName && Boolean(errors.folderName)}
                helperText={touched.folderName && errors.folderName}
                sx={{ mb: 3 }}
              />
              <TextField
                margin="normal"
                fullWidth
                id="description"
                label="Description"
                name="description"
                multiline
                rows={4}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
                sx={{ mb: 3 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CreateNewFolderIcon />}
                  sx={{ px: 3, py: 1 }}
                  disabled={isSubmitting || !isValid || !dirty || !values.folderName}
                >
                  {isSubmitting ? 'Creating...' : 'Create'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleBack}
                  sx={{ px: 3, py: 1 }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
    </Box>
    </Box>
  );
};

export default CreateFolder;